import { createAsyncThunk } from "@reduxjs/toolkit";
import { createFormSlice } from "../../../shared/slice";
import {
    fetchContactById,
    fetchDepartmentById,
    transformDraftItems,
} from "../../shared/slice/entities";
import { fetchPurchaseInvoiceDraft } from "../../../../../../../../../services/dataEntering/purchaseInvoiceDraft/api";
import { PurchaseInvoiceDraft } from "../../../../../../../../../services/dataEntering/purchaseInvoiceDraft/types";
import { InvoiceFormState, PurchaseInvoiceTypeEnum } from "./types";
import { AppDispatch, GetRootState } from "../../../../../../../../../shared/redux/types";

const toFormState = async (
    draft: PurchaseInvoiceDraft,
    accountId: number,
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<InvoiceFormState> => {
    const [seller, department, items] = await Promise.all([
        fetchContactById(draft.sellerId, accountId, dispatch, getState),
        fetchDepartmentById(draft.departmentId, accountId, dispatch, getState),
        transformDraftItems(draft.items, accountId, dispatch, getState),
    ]);

    return {
        seller,
        publicId: draft.publicId ?? "",
        acquisitionCountryId: draft.acquisitionCountryId,
        bankCountryId: draft.bankCountryId,
        type: draft.type as PurchaseInvoiceTypeEnum,
        externalId: "",
        note: draft.note ?? "",
        currencyId: draft.currencyId ?? "",
        paymentMethod: draft.paymentMethod,
        ibanOrAccount: draft.ibanOrAccount ?? "",
        contract: null,
        department,
        linkedDocument: draft.linkedDocument ?? null,
        variableSymbol: draft.variableSymbol ?? "",
        constantSymbol: draft.constantSymbol ?? "",
        specificSymbol: draft.specificSymbol ?? "",
        exchangeRate: draft.exchangeRate == null ? "" : String(draft.exchangeRate),
        items,
        totalAmount: draft.totalAmount,
        rounding: draft.rounding ?? null,
        vat1Amount: draft.vat1Amount,
        vat2Amount: draft.vat2Amount,
        vat3Amount: draft.vat3Amount,
        dateOfReceipt: draft.dateOfReceipt,
        dueDate: draft.dueDate,
        taxPaymentDate: draft.taxPaymentDate,
    };
};

export const setDefaults = createAsyncThunk(
    "purchaseInvoiceForm/setDefaults",
    async (accountId: number, { dispatch, getState, rejectWithValue }) => {
        try {
            const { documentId } = (getState as GetRootState)().dataEntering;
            const draft = await fetchPurchaseInvoiceDraft({
                dispatch: dispatch as AppDispatch,
                accountId,
                documentId,
            });

            return await toFormState(
                draft,
                accountId,
                dispatch as AppDispatch,
                getState as GetRootState,
            );
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

const slice = createFormSlice("purchaseInvoiceForm", (builder) => {
    builder
        .addCase(setDefaults.fulfilled, (draftState, action) => {
            draftState.data = action.payload;
            draftState.status = "initialized";
        })
        .addCase(setDefaults.pending, (draftState) => {
            draftState.data = {};
            draftState.status = "initializing";
        })
        .addCase(setDefaults.rejected, (draftState) => {
            draftState.data = {};
            draftState.status = "error";
        });
});

export const { name, actions, reducer } = slice;
